import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Section from '../Section';
import Breadcrumb from '../Breadcrumb';
import { Icon } from '@iconify/react';
import Spacing from '../Spacing';
import Sidebar from '../Sidebar';
import { getArticleBySlug } from '../../helpers/GetBlogArticle';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import languages from '../../config/languages';
import { BlogArticleContext } from '../../contexts/BlogArticleContext';
import ErrorPage from './ErrorPage';

export default function BlogDetails() {
  const { slug } = useParams();
  const location = useLocation();
  const { t, i18n, ready } = useTranslation();

  // Se extrae el código de idioma de la URL
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const languageCode = pathSegments[0];
  const isLanguageValid = languages.hasOwnProperty(languageCode);
  const language = isLanguageValid ? languageCode : 'es';

  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Agregar el estado para la URL canónica
  const [canonicalUrl, setCanonicalUrl] = useState('');

  const { setCurrentBlogArticle } = useContext(BlogArticleContext);

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const data = await getArticleBySlug(slug, language);
        if (!data) {
          setError(t('article_not_found') || "404 - Artículo no encontrado.");
          setIsLoading(false);
          return;
        }
        if (data.locale !== language) {
          setError(t('article_not_found') || "404 - Artículo no encontrado.");
          setIsLoading(false);
          return;
        }
        const mainArticle = data.parent_id ? data.main_article : data;
        setArticle(data);
        setCurrentBlogArticle(mainArticle);
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setError(t('error_fetching_article') || "404 - Error al cargar el artículo.");
        setIsLoading(false);
      }
    };
    fetchArticle();
  }, [slug, language, setCurrentBlogArticle, t]);

  // Calcular la URL canónica cuando el componente se monta
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCanonicalUrl(window.location.origin + window.location.pathname);
    }
  }, []);

  if (!ready) {
    return null; // Aquí puedes mostrar un spinner o indicador de carga
  }
  if (isLoading) return <p>{t('loading_article')}</p>;
  if (error) return <ErrorPage message={error} />;
  if (!article) return <p>{t('article_not_found')}</p>;

  const bgUrl = '/images/home_1/degradadobckg.webp';

  return (
    <>
      <Helmet>
        <title>{article.meta_title || article.title}</title>
        <meta name="description" content={article.meta_description || ''} />
        {/* Agregar la URL canónica */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Helmet>
      <Section
        style={{
          backgroundImage: `url(${bgUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '50px 0 100px',
        }}
      />
      
      <Section topMd={50} bottomMd={54} bottomLg={54}>
        <Breadcrumb title={article.title} />
      </Section>
      
      <div className="container">
        <div className="row">
          {/* Contenido principal - Izquierda */}
          <div className="col-lg-8">
            {/* Imagen principal */}
            <div className="mb-4 d-flex justify-content-center">
              <img
                src={`https://dashboard.gestlifesurrogacy.com/${article.image}`}
                alt={article.title}
                className="cs_radius_20"
                style={{ width: '90%', height: 'auto' }}
              />
            </div>
            
            {/* Título del contenido */}
            <div className="cs_blog_details" style={{ maxWidth: '90%', margin: '0 auto' }}>
              
              {/* Contenido del artículo */}
              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            </div>
            
            <Spacing md="40" />
            
            {/* Información del blog y redes sociales - al final */}
            <div className="cs_blog_details_info">
              <div className="cs_blog_details_info_left">
                <div className="cs_blog_details_date">
                  {new Date(article.date).toLocaleDateString(
                    i18n.language === 'es'
                      ? 'es-ES'
                      : i18n.language === 'fr'
                      ? 'fr-FR'
                      : i18n.language === 'pt'
                      ? 'pt-PT'
                      : i18n.language === 'it'
                      ? 'it-IT'
                      : i18n.language === 'de'
                      ? 'de-DE'
                      : 'en-US',
                    {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    }
                  )}
                </div>
              </div>
              <div className="cs_social_links_wrap">
                <h2>{t('share')}:</h2>
                <div className="cs_social_links">
                  <Link to="/">
                    <Icon icon="fa-brands:facebook-f" />
                  </Link>
                  <Link to="/">
                    <Icon icon="fa-brands:linkedin-in" />
                  </Link>
                </div>
              </div>
            </div>
            
            <Spacing md="85" />
          </div>
          
          {/* Sidebar - Derecha */}
          <div className="col-lg-4">
            <Sidebar />
          </div>
        </div>
      </div>
    </>
  );
}